import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowRight, Users, PaperPlaneTilt, DownloadSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";

export default function Dashboard() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", mpm: 10, notification_email: "", notification_phone: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
    } catch (e) {
      // ignore transient
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const { data } = await api.post("/campaigns", {
        name: form.name,
        mpm: Number(form.mpm),
        notification_email: form.notification_email.trim() || null,
        notification_phone: form.notification_phone.trim() || null,
      });
      toast.success("Campaign created");
      setShowForm(false);
      setForm({ name: "", mpm: 10, notification_email: "", notification_phone: "" });
      navigate(`/campaigns/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setCreating(false);
    }
  };

  const exportStatus = async (c, e) => {
    e.stopPropagation();
    try {
      const res = await api.get(`/campaigns/${c.id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(c.name || "campaign").replace(/[^a-z0-9-_]/gi, "_")}_status.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Status exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  return (
    <Layout>
      <div className="flex items-end justify-between border-b border-sv-border pb-6">
        <div>
          <p className="font-mono text-xs text-sv-muted uppercase tracking-widest">// operations</p>
          <h1 className="font-display font-black text-4xl tracking-tight uppercase mt-1">Campaigns</h1>
        </div>
        <button
          data-testid="new-campaign-button"
          onClick={() => setShowForm((s) => !s)}
          className="flex items-center gap-2 h-11 px-5 bg-sv-ink text-sv-onink font-semibold text-sm hover:bg-sv-secondary transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-sv-ink"
        >
          <Plus size={18} weight="bold" />
          New Campaign
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={create}
          data-testid="create-campaign-form"
          className="mt-6 border border-sv-border bg-sv-surface p-6 space-y-4 sv-fade-up"
        >
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-4">
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Campaign name</label>
              <input
                data-testid="campaign-name-input"
                required
                autoFocus
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Q3 Outreach"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Msgs / minute</label>
              <input
                data-testid="campaign-mpm-input"
                type="number"
                min="1"
                max="600"
                required
                value={form.mpm}
                onChange={(e) => setForm({ ...form, mpm: e.target.value })}
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Notification email <span className="text-sv-muted normal-case">(positive-lead alerts)</span></label>
              <input
                data-testid="campaign-notify-email-input"
                type="email"
                value={form.notification_email}
                onChange={(e) => setForm({ ...form, notification_email: e.target.value })}
                placeholder="you@company.com"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
            <div>
              <label className="font-mono text-xs text-sv-secondary uppercase">Notification phone <span className="text-sv-muted normal-case">(alert SMS)</span></label>
              <input
                data-testid="campaign-notify-phone-input"
                value={form.notification_phone}
                onChange={(e) => setForm({ ...form, notification_phone: e.target.value })}
                placeholder="+19285435493"
                className="mt-1 w-full h-11 bg-sv-bg border border-sv-border px-3 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sv-ink focus:border-sv-ink transition-colors duration-150"
              />
            </div>
          </div>
          <button
            data-testid="submit-campaign-button"
            type="submit"
            disabled={creating}
            className="h-11 px-6 bg-sv-live text-black font-semibold text-sm hover:brightness-110 transition-all duration-150 disabled:opacity-50"
          >
            {creating ? "…" : "Create campaign"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="font-mono text-sm text-sv-muted mt-10">LOADING…</p>
      ) : campaigns.length === 0 ? (
        <div className="mt-10 border border-dashed border-sv-border p-16 text-center">
          <p className="font-mono text-sm text-sv-muted">NO_CAMPAIGNS_YET</p>
          <p className="text-sv-secondary text-sm mt-2">Create a campaign and upload your contacts CSV to begin.</p>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {campaigns.map((c) => {
            const s = c.stats || {};
            const done = (s.sent || 0) + (s.failed || 0);
            const pct = s.total ? Math.round((done / s.total) * 100) : 0;
            return (
              <div
                key={c.id}
                role="button"
                tabIndex={0}
                data-testid={`campaign-card-${c.id}`}
                onClick={() => navigate(`/campaigns/${c.id}`)}
                onKeyDown={(e) => { if (e.key === "Enter") navigate(`/campaigns/${c.id}`); }}
                className="text-left border border-sv-border bg-sv-surface p-5 hover:bg-sv-hover transition-colors duration-150 group focus:outline-none focus:ring-1 focus:ring-sv-ink cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display font-bold text-xl tracking-tight truncate">{c.name}</h3>
                  <StatusBadge status={c.status} />
                </div>
                <div className="mt-4 flex items-center gap-4 font-mono text-xs text-sv-secondary">
                  <span className="flex items-center gap-1.5">
                    <Users size={14} weight="bold" /> {s.total || 0}
                  </span>
                  <span className="flex items-center gap-1.5 text-sv-live">
                    <PaperPlaneTilt size={14} weight="bold" /> {s.sent || 0}
                  </span>
                  <span className="text-sv-muted">{c.mpm}/min</span>
                </div>
                <div className="mt-4 h-1.5 bg-sv-bg border border-sv-border overflow-hidden">
                  <div className="h-full bg-sv-live transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono text-xs text-sv-muted">{pct}% complete</span>
                  <div className="flex items-center gap-3">
                    <button
                      data-testid={`export-status-${c.id}`}
                      onClick={(e) => exportStatus(c, e)}
                      title="Download a CSV snapshot of every contact's current status"
                      className="flex items-center gap-1.5 font-mono text-xs text-sv-secondary hover:text-sv-ink border border-sv-border px-2.5 py-1.5 hover:bg-sv-hover transition-colors duration-150 focus:outline-none focus:ring-1 focus:ring-sv-ink"
                    >
                      <DownloadSimple size={14} weight="bold" /> Export status
                    </button>
                    <ArrowRight
                      size={16}
                      weight="bold"
                      className="text-sv-muted group-hover:text-sv-ink group-hover:translate-x-1 transition-all duration-150"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}
